import React, {useEffect, useState} from 'react';
import {Button, message, Table, Tag} from 'antd';
import {useMutation} from '@apollo/client';
import {useHashState} from '../../../utils/hashState';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {DeliveryTaskReviewWithCountType} from './graphql/queries/getDeliveryTaskReviewsWithCount';
import {REVIEW_START_DELIVERY} from './graphql/mutations/startDelivery';
import {REVIEW_DSP_START_DELIVERY} from './graphql/mutations/reviewDspStartDelivery';
import DepartureReviewFilter from './components/DepartureReviewFilter';
import DepartureReviewDrawer from './components/DepartureReviewDrawer';
import DepartureDspReviewDrawer from './components/DepartureDspReviewDrawer';
import ScanOtherDriverParcelDrawer from './components/ScanOtherDriverParcelDrawer';
import {ReviewParcelType} from './graphql/types/ReviewParcelType';
import {ReviewInitialValue} from './graphql/types/ReviewFilterType';
import dayjs from "dayjs";
import {DriverType} from "../../../graphql/types/AreaPlanningType";
import {DspType} from "../../../graphql/types/DspType";
import {formatTimestampToDateTime, getLastWeekDateRange} from "../../../utils/dateTimeUtils";
import {useWmsParcelQuery} from "./hooks/useWmsReviweQuery";
import {useTranslation} from 'react-i18next';
import {useGetAllAccessHubAndWarehousesForRole} from "../../../hooks/useWarehouseQuery";
import {
    DepartureDriverType,
    useAccountDepartureDriversByWarehouseId
} from "./graphql/queries/getAccountDeliveryDriversByWarehouseIdForReview";
import {useDspsByWarehouseId} from "../../delivery/accounts/graphql/queries/getDspsByWarehouseId";
import {getUserInfo} from "../../../utils/auth";

const DepartureReviewPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const { warehouses } = useGetAllAccessHubAndWarehousesForRole({
        fetchPolicy: 'cache-first'
    });
    const [tempFilters, setTempFilters] = useHashState(ReviewInitialValue);
    const [filters, setFilters] = useHashState(ReviewInitialValue);
    // - undefined：未初始化，默认最近一周
    // - null：用户手动清空（点击叉号），保持为空
    const getEffectiveTaskDate = (value: any): [string | null, string | null] | null => {
        if (value === null) return null;
        const isValid = value &&
            Array.isArray(value) &&
            value.length === 2 &&
            value[0] !== null &&
            value[1] !== null;
        // 复用 ReviewFilter 的日期格式 YYYY/MM/DD
        return isValid ? (value as [string | null, string | null]) : getLastWeekDateRange('YYYY/MM/DD');
    };
    const [columnData, setColumnData] = useState<ReviewParcelType[]>([])
    const [selectedRecord, setSelectedRecord] = useState<ReviewParcelType | undefined>();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [dspDrawerVisible, setDspDrawerVisible] = useState(false);
    const [scanOtherDrawerVisible, setScanOtherDrawerVisible] = useState(false);
    const [loadingStates, setLoadingStates] = useState<Map<string, boolean>>(new Map());

    // 使用新的 hook 获取 driver 数据
    const {
        accountDeliveryDriversByWarehouseId: driversData,
        loading: driversLoading
    } = useAccountDepartureDriversByWarehouseId(currentWarehouse?.id ? parseInt(currentWarehouse.id) : null);

    // 使用新的 hook 获取 DSP 数据
    const {
        dspsByWarehouseId: dspsData,
        loading: dspsLoading
    } = useDspsByWarehouseId(currentWarehouse?.id ? parseInt(currentWarehouse.id) : null);

    const [allDrivers, setAllDrivers] = useState<DriverType[]>([]);
    const [dspOptions, setDspOptions] = useState<DspType[]>([]);

    useEffect(() => {
        if (driversData) {
            const driverList: DriverType[] = driversData.map((driver: DepartureDriverType) => ({
                accountId: driver?.accountId?.toString() || '',
                name: driver?.driverName || 'Unknown',
                phone: driver?.driverPhone?.toString() || '',
                isOnDuty: driver?.isOnDuty || false
            }));
            setAllDrivers(driverList);
        }
    }, [driversData]);

    useEffect(() => {
        if (dspsData) {
            const dspList: DspType[] = dspsData.map(dsp => ({
                ...dsp,
                warehouseIds: dsp.warehouses.map(warehouse => Number(warehouse.id))
            }));
            setDspOptions(dspList);
        }
    }, [dspsData]);

    const{
        reviewData,
        loading,
        refetch,
        buildQueryVariables
    } = useWmsParcelQuery(
        {
            ...filters,
            taskDate: getEffectiveTaskDate(filters.taskDate)
        } as any,
        currentWarehouse?.id ? parseInt(currentWarehouse.id) : 0
    );

    useEffect(() => {
        if (currentWarehouse?.id) {
            refetch(buildQueryVariables());
        }
    }, [filters, currentWarehouse?.id]);

    // ID 转名称的函数
    const getNameById = (id: string | number, type: 'warehouse' | 'dsp' | 'driver') => {
        const numId = typeof id === 'string' ? parseInt(id) : id;
        switch (type) {
            case 'warehouse':
                return warehouses.find(w => Number(w.id) === numId)?.name || id;
            case 'dsp':
                return (dspOptions && !dspsLoading) ? (dspOptions.find(d => Number(d.id) === numId)?.name || id) : id;
            case 'driver':
                const driver = (allDrivers && !driversLoading) ? allDrivers.find(d => Number(d.accountId) === numId) : null;
                return driver ? `${driver.name}` : id;
            default:
                return id;
        }
    };

    const [startDeliveryMutation] = useMutation(REVIEW_START_DELIVERY);
    const [reviewDspStartDeliveryMutation] = useMutation(REVIEW_DSP_START_DELIVERY);

    const handleFilter = () => {
        setFilters(tempFilters);
    };

    const handleResetFilters = () => {
        // 对于非HUB用户，保持当前仓库选择
        const resetValue = {
            ...ReviewInitialValue,
            warehouseId: currentWarehouse?.id?.toString() || '',
            // 默认日期不写入 URL/hash：保持 undefined
            taskDate: undefined
        };
        setTempFilters(resetValue);
        setFilters(resetValue);
    };

    useEffect(() => {
        if (loading || dspsLoading || driversLoading){
            setColumnData([]);
            return;
        }
        const mappedData = reviewData?.map((review: DeliveryTaskReviewWithCountType) => {
            return {
                id: review.id,
                taskDate: review.taskDate,
                warehouseName: review.warehouseName || '',
                depId: 0,
                dspId: review.dspId,
                dspName: String(getNameById(review.dspId, 'dsp') || ''),
                dspAccountName:review.dspName,
                dspPhone:review.dspPhone || '',
                submitType: review.submitType,
                reviewDimension: review.submitType === 1 ? t('wms.review.columns.driverDimension') : t('wms.review.columns.dspDimension'),
                driverName: String(getNameById(review.accountId, 'driver') || ''),
                driverPhone: allDrivers.find(d => d.accountId === review.accountId)?.phone || '',
                accountId: review.accountId,
                accountIdList: review.accountIdList,
                dspLevelReviewIdList: review.dspLevelReviewIdList,
                unscannedCount: review.unScannedCount,
                scanOtherDriverParcelCount: review.scanOtherDriverParcelCount || 0,
                scanOtherDriverParcelDisplayCount:
                    review.scanOtherDriverParcelDisplayCount ?? review.scanOtherDriverParcelCount ?? 0,
                otherDriverScanCurrentDriverParcelCount: review.otherDriverScanCurrentDriverParcelCount || 0,
                deliveryTaskId: null,
                // 保留原始时间戳：列渲染时统一格式化，避免重复格式化导致 Invalid Date
                createTime: review.createdAt,
                formattedTaskDate: dayjs.unix(parseInt(review.taskDate)).format('YYYY/MM/DD')
            };
        }) || [];
        setColumnData(mappedData);
    }, [reviewData, loading, dspOptions, dspsLoading, allDrivers, driversLoading, t]);


    const handleNumberClick = (record: ReviewParcelType) => {
        if (record.unscannedCount === 0) return;
        console.log('handleNumberClick', record)
        setSelectedRecord(record);
        // 根据 submitType 判断打开哪个 drawer
        if (record.submitType === 1) {
            // 司机维度
            console.log("司机维度")
            setDrawerVisible(true);
            setDspDrawerVisible(false);
        } else if (record.submitType === 2) {
            // 车队维度
            console.log("车队维度")
            setDspDrawerVisible(true);
            setDrawerVisible(false);
        }
    };

    const handleDrawerClose = () => {
        setDrawerVisible(false);
        setDspDrawerVisible(false);
        setScanOtherDrawerVisible(false);
        setSelectedRecord(undefined);
    };

    const handleStartDelivery = async (record: ReviewParcelType) => {
        try {
            setLoadingStates(prev => new Map(prev.set(record.id, true)));
            
            if (record.unscannedCount === 0) {
                // 根据 submitType 判断调用哪个接口
                if (record.submitType === 1) {
                    // 司机维度：调用 reviewStartDelivery
                    const currentUser = getUserInfo();
                    const {data} = await startDeliveryMutation({
                        variables: {
                            deliverAccountId: parseInt(record.accountId),
                            id: parseInt(record.id),
                            operatorName: currentUser?.account?.name || null
                        },
                    });
                    if (data){
                        message.success(t('wms.review.messages.success'));
                        // 刷新列表数据
                        refetch(buildQueryVariables());
                    }
                } else if (record.submitType === 2) {
                    // 车队维度：调用 reviewDspStartDelivery
                    const driverIdList = record.accountIdList || [];
                    const dspLevelReviewIdList = record.dspLevelReviewIdList || [];
                    if (driverIdList.length === 0 || dspLevelReviewIdList.length === 0) {
                        message.error('车队维度数据缺少必要信息');
                        return;
                    }
                    const currentUser = getUserInfo();
                    const {data} = await reviewDspStartDeliveryMutation({
                        variables: {
                            deliverAccountIdList: driverIdList,
                            dspLevelReviewIdList: dspLevelReviewIdList,
                            operatorName: currentUser?.account?.name || null
                        },
                    });
                    if (data){
                        message.success(t('wms.review.messages.success'));
                        // 刷新列表数据
                        refetch(buildQueryVariables());
                    }
                }
            }
        } catch (error) {
            console.error('Start delivery error:', error);
            message.error(t('wms.review.messages.startDeliveryError'));
        } finally {
            setLoadingStates(prev => {
                const newMap = new Map(prev);
                newMap.delete(record.id);
                return newMap;
            });
        }
    };

    const columns = [
        {
            title: t('wms.review.columns.createTime'),
            dataIndex: 'createTime',
            key: 'createTime',
            align: 'left' as const,
            width: 100,
            ellipsis: false,
            render: (text: string) => {
                const formatted = formatTimestampToDateTime(text);
                return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
            }
        },
        {
            title: t('wms.review.columns.taskDate'),
            dataIndex: 'formattedTaskDate',
            key: 'formattedTaskDate',
            align: 'left' as const,
            width: 100,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('wms.review.columns.whs'),
            dataIndex: 'warehouseName',
            key: 'warehouseName',
            align: 'left' as const,
            width: 100,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('wms.review.columns.dsp'),
            dataIndex: 'dspName',
            key: 'dspName',
            align: 'left' as const,
            width: 100,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title:  t('wms.review.columns.dimension'),
            dataIndex: 'reviewDimension',
            key: 'reviewDimension',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('wms.review.columns.driverAndDsp'),
            dataIndex: 'driverName',
            key: 'driverName',
            align: 'left' as const,
            width: 150,
            ellipsis: false,
            render: (value: string, record: ReviewParcelType) => {
                // 如果是司机维度（submitType === 1），显示司机名称+手机号
                if (record.submitType === 1) {
                    return (
                        <div style={{ whiteSpace: 'nowrap' }}>
                            <span className={'font-bold'}>{record.driverName || t('wms.review.unknown')}</span>
                            <br />
                            <span className={'text-xs text-gray-500'}> +1 {record.driverPhone || t('wms.review.na')}</span>
                        </div>
                    );
                }
                // 如果是DSP维度（submitType === 2），显示DSP名称
                return (
                    <div style={{ whiteSpace: 'nowrap' }}>
                        <span className={'font-bold'}>{record.dspAccountName || t('wms.review.unknown')}</span>
                    </div>
                );
            }
        },
        {
            title: t('wms.review.columns.driverUnscanned'),
            dataIndex: 'unscannedCount',
            key: 'unscannedCount',
            align: 'left' as const,
            width: 120,
            ellipsis: false,
            render: (value: number, record: ReviewParcelType) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                    <span
                        style={{
                            cursor: value > 0 ? 'pointer' : 'default',
                            color: value > 0 ? '#1890ff' : 'inherit',
                            whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleNumberClick(record)}
                    >
                        {value}
                    </span>
                    {record.submitType === 1 && (record.otherDriverScanCurrentDriverParcelCount || 0) > 0 && (
                        <Tag color="red" style={{ marginInlineEnd: 0 }}>
                            {t('wms.review.columns.scannedByOthers', { count: record.otherDriverScanCurrentDriverParcelCount })}
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: t('wms.review.columns.driverOverScanned'),
            dataIndex: 'scanOtherDriverParcelDisplayCount',
            key: 'scanOtherDriverParcelDisplayCount',
            align: 'left' as const,
            width: 160,
            ellipsis: false,
            render: (_value: number, record: ReviewParcelType) => {
                const n = record.scanOtherDriverParcelDisplayCount ?? 0;
                return record.submitType === 1 && n > 0 ? (
                    <Tag
                        color="gold"
                        style={{ marginInlineEnd: 0, cursor: 'pointer' }}
                        onClick={() => {
                            setSelectedRecord(record);
                            setScanOtherDrawerVisible(true);
                        }}
                    >
                        {t('wms.review.columns.scannedOtherDriverPackages', { count: n })}
                    </Tag>
                ) : (
                    <span style={{ whiteSpace: 'nowrap' }}>{n}</span>
                );
            },
        },
        {
            title: t('wms.review.columns.action'),
            key: 'action',
            align: 'left' as const,
            width: 120,
            fixed: 'right' as const,
            ellipsis: false,
            render: (_: any, record: ReviewParcelType) => (
                (() => {
                    const isDriverDimension = record.submitType === 1;
                    const canStartForDriver = record.unscannedCount === 0 && (record.scanOtherDriverParcelCount || 0) === 0;
                    const canStart = isDriverDimension ? canStartForDriver : record.unscannedCount === 0;
                    return (
                <Button
                    type="primary"
                    size="small"
                    loading={loadingStates.get(record.id) || false}
                    onClick={() => handleStartDelivery(record)}
                    disabled={!canStart}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('wms.review.buttons.startDelivery')}</span>
                </Button>
                    );
                })()
            ),
        },
    ];

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                <DepartureReviewFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                    dsps={dspOptions}
                    drivers={allDrivers}
                />

                <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                    <Table
                        columns={columns}
                        dataSource={columnData}
                        loading={loading || dspsLoading || driversLoading}
                        rowKey="id"
                        pagination={false}
                        scroll={{x: 'max-content'}}
                        size="middle"
                        tableLayout="auto"
                     className="mb-4"/>
                </div>

                {/* 左下角合计 */}
                <div className="flex justify-between items-center">
                    <div className="text-gray-500">
                        {t('table.pagination.totalItems', { total: columnData.length })}
                    </div>
                </div>

                <DepartureReviewDrawer
                    visible={drawerVisible}
                    title={`${selectedRecord?.warehouseName || ''} - ${selectedRecord?.dspName || ''} - ${selectedRecord?.formattedTaskDate} - ${t('wms.review.columns.driverUnscanned')}`}
                    record={selectedRecord}
                    onClose={handleDrawerClose}
                    onDataRefresh={refetch}
                    allDrivers={allDrivers}
                />

                <DepartureDspReviewDrawer
                    visible={dspDrawerVisible}
                    title={`${selectedRecord?.warehouseName || ''} - ${selectedRecord?.dspName || ''} - ${selectedRecord?.formattedTaskDate} - ${t('wms.review.columns.driverUnscanned')}`}
                    record={selectedRecord}
                    onClose={handleDrawerClose}
                    onDataRefresh={refetch}
                />

                <ScanOtherDriverParcelDrawer
                    visible={scanOtherDrawerVisible}
                    title={`${selectedRecord?.warehouseName || ''} - ${selectedRecord?.dspName || ''} - ${selectedRecord?.formattedTaskDate} - ${t('wms.review.columns.driverOverScanned')}`}
                    driverAccountId={selectedRecord?.accountId ? parseInt(selectedRecord.accountId) : undefined}
                    onClose={handleDrawerClose}
                    onDataRefresh={refetch}
                />
            </div>
    );
};

export default DepartureReviewPage;
